import { createSlice } from '@reduxjs/toolkit';
import { equipItemProp2 } from '../../interfaces/base';
import { getLocationKey } from '../../../utils/cartUtils';

type filterDateType = {
  pickupDate?: string;
  pickupTime?: string;
  returnDate?: string;
  returnTime?: string;
};

type initialStateType = {
  headerTheme: string;
  showInfoModal: boolean;
  filterDate: filterDateType | null;
  equipInfo: any;
  cartItems: equipItemProp2[];
  confirmItems: equipItemProp2[];
  confirmEquipFee: {
    rentalFinalTotal?: number;
  };
  equipFee: {
    rentalFinalTotal?: number;
  };
  equipRate: {
    rentalFinalTotal?: number;
  };
  partnerInfo: {
    configs?: any;
  };
  partnerStore: {
    id?: string;
  }
  partnerTrackCode: {
    id?: string;
  }
  checkoutForm: {},
  customerCheckId: number[] | string [];
  coValidation: {
    customerNameFirst?: boolean;
  },
  customerId: number,
  bookingId: number,
  bookingRefId: string,
  confirmId: string,
  customerAddId: number,
  isLoading: boolean;
  locations: { label: string, value: string }[];
  selLocation: number | string;
  discountCode: string; // Applied discount/promo code
  addonServicesByAssetId: Record<string, any[]>;
  selectedAddonServiceIdsByAssetId: Record<string, number[]>;
  selectedCheckoutLocationId: string | null;
  equipFeeByAssetId: Record<string, any>;
}

const initialState: initialStateType = {
  headerTheme: 'white',
  showInfoModal: false,
  filterDate: {},
  equipInfo: {},
  equipRate: {},
  equipFee: {},
  cartItems: [],
  confirmEquipFee: {},
  confirmItems: [],
  partnerInfo: {},
  partnerStore: {},
  partnerTrackCode: {},
  checkoutForm: {},
  customerCheckId: ['1'],
  confirmId: '',
  customerId: 0,
  customerAddId: 0,
  bookingId: 0,
  bookingRefId: '',
  coValidation: {},
  isLoading: false,
  locations: [],
  selLocation: "",
  discountCode: '', // Initialize with empty string
  addonServicesByAssetId: {},
  selectedAddonServiceIdsByAssetId: {},
  selectedCheckoutLocationId: null,
  equipFeeByAssetId: {},
};

export const settingSlice = createSlice({
  name: 'setting',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setHeaderTheme: (state, { payload }) => {
      state.headerTheme = payload;
    },
    setShowInfoModal: (state, { payload }) => {
      state.showInfoModal = payload;
    },
    setEquipInfo: (state, { payload }) => {
      state.equipInfo = payload;
    },
    setAddCart: (state, { payload }) => {
	    state.confirmItems = [];
	    state.confirmEquipFee= {};
      state.cartItems = [payload];
      state.selectedCheckoutLocationId = null;
      state.equipFeeByAssetId = {};
      
      state.bookingId = 0;
      state.bookingRefId = '';
      state.confirmId = '';
      state.discountCode = '';
      state.checkoutForm = { ...state.checkoutForm, discountCode: '' };
    },
    addCartItem: (state, { payload }) => {
      const assetId = String(payload?.assetId ?? '');
      if (!assetId) return;

      const locationKey = getLocationKey(payload);
      const alreadyInCart = state.cartItems.some((item: any) => {
        if (String(item?.assetId) === assetId) return true;
        if (locationKey && getLocationKey(item) === locationKey) return true;
        return false;
      });
      if (alreadyInCart) return;

      state.cartItems.push(payload);
    },
    replaceCartItemAtLocation: (state, { payload }) => {
      const locationKey = getLocationKey(payload);
      if (!locationKey) return;

      const removedAssetIds = state.cartItems
        .filter((item: any) => getLocationKey(item) === locationKey)
        .map((item: any) => String(item?.assetId))
        .filter(Boolean);

      state.cartItems = state.cartItems.filter(
        (item: any) => getLocationKey(item) !== locationKey,
      );
      state.cartItems.push(payload);

      const incomingAssetId = String(payload?.assetId ?? '');

      removedAssetIds.forEach((assetId) => {
        if (incomingAssetId && assetId === incomingAssetId) return;
        delete state.equipFeeByAssetId[assetId];
        delete state.addonServicesByAssetId[assetId];
        delete state.selectedAddonServiceIdsByAssetId[assetId];
      });

      state.confirmItems = [];
      state.confirmEquipFee = {};
      state.bookingId = 0;
      state.bookingRefId = '';
      state.confirmId = '';
      state.discountCode = '';
      state.checkoutForm = { ...state.checkoutForm, discountCode: '' };

      if (state.selectedCheckoutLocationId === locationKey) {
        state.selectedCheckoutLocationId = null;
      }
    },
    setCartItems: (state, { payload }) => {
      state.cartItems = payload;
      state.selectedCheckoutLocationId = null;
      state.equipFeeByAssetId = {};
      state.equipFee = {};
      state.confirmItems = [];
      state.confirmEquipFee = {};
      state.bookingId = 0;
      state.bookingRefId = '';
      state.confirmId = '';
      state.discountCode = '';
      state.checkoutForm = { ...state.checkoutForm, discountCode: '' };
    },
    setConfirmItems: (state, { payload }) => {
      state.confirmItems = payload;
    },
    setFilterPickupDate: (state, { payload }) => {
      state.filterDate = {
        ...state.filterDate,
        pickupDate: payload,
      };
    },
    setFilterPickupTime: (state, { payload }) => {
      state.filterDate = {
        ...state.filterDate,
        pickupTime: payload,
      };
    },
    setFilterReturnDate: (state, { payload }) => {
      state.filterDate = {
        ...state.filterDate,
        returnDate: payload,
      };
    },
    setFilterReturnTime: (state, { payload }) => {
      state.filterDate = {
        ...state.filterDate,
        returnTime: payload,
      };
    },
    setFilterDate: (state, { payload }) => {
      state.filterDate = payload;
    },
    setRemoveCart: (state) => {
      state.cartItems = [];
      state.equipInfo = {};
      state.equipRate = {};
      state.equipFee = {};
      state.equipFeeByAssetId = {};
      state.addonServicesByAssetId = {};
      state.selectedAddonServiceIdsByAssetId = {};
      state.selectedCheckoutLocationId = null;
    },
    removeCartItemByAssetId: (state, { payload }) => {
      const assetId = String(payload ?? '');
      if (!assetId) return;

      state.cartItems = state.cartItems.filter(
        (item: any) => String(item?.assetId) !== assetId,
      );
      delete state.equipFeeByAssetId[assetId];
      delete state.addonServicesByAssetId[assetId];
      delete state.selectedAddonServiceIdsByAssetId[assetId];

      if (
        state.selectedCheckoutLocationId &&
        !state.cartItems.some(
          (item: any) => getLocationKey(item) === state.selectedCheckoutLocationId,
        )
      ) {
        state.selectedCheckoutLocationId = null;
      }

      if (!state.cartItems.length) {
        state.equipInfo = {};
        state.equipRate = {};
        state.equipFee = {};
      }
    },
    removeCartItemsAtLocation: (state, { payload }) => {
      const locationKey = payload ? String(payload) : '';
      if (!locationKey) return;

      const removedAssetIds = state.cartItems
        .filter((item: any) => getLocationKey(item) === locationKey)
        .map((item: any) => String(item?.assetId))
        .filter(Boolean);

      state.cartItems = state.cartItems.filter(
        (item: any) => getLocationKey(item) !== locationKey,
      );

      removedAssetIds.forEach((assetId) => {
        delete state.equipFeeByAssetId[assetId];
        delete state.addonServicesByAssetId[assetId];
        delete state.selectedAddonServiceIdsByAssetId[assetId];
      });

      state.selectedCheckoutLocationId = null;

      if (!state.cartItems.length) {
        state.equipInfo = {};
        state.equipRate = {};
        state.equipFee = {};
        state.equipFeeByAssetId = {};
      }
    },
    removeCartItemsByAssetIds: (state, { payload }) => {
      const assetIds = (Array.isArray(payload) ? payload : [])
        .map((id) => String(id))
        .filter(Boolean);

      if (!assetIds.length) return;

      const assetIdSet = new Set(assetIds);

      state.cartItems = state.cartItems.filter(
        (item: any) => !assetIdSet.has(String(item?.assetId)),
      );

      assetIds.forEach((assetId) => {
        delete state.equipFeeByAssetId[assetId];
        delete state.addonServicesByAssetId[assetId];
        delete state.selectedAddonServiceIdsByAssetId[assetId];
      });

      state.selectedCheckoutLocationId = null;

      if (!state.cartItems.length) {
        state.equipInfo = {};
        state.equipRate = {};
        state.equipFee = {};
        state.equipFeeByAssetId = {};
        state.addonServicesByAssetId = {};
        state.selectedAddonServiceIdsByAssetId = {};
      }
    },
    setEquipRate: (state, { payload }) => {
      state.equipRate = payload;
    },
    setEquipFee: (state, { payload }) => {
      state.equipFee = payload;
    },
    setEquipFeeByAssetId: (state, { payload }) => {
      state.equipFeeByAssetId = payload || {};
    },
    setEquipFeeForAsset: (state, { payload }) => {
      const assetId = String(payload?.assetId ?? '');
      if (!assetId) return;
      state.equipFeeByAssetId[assetId] = payload?.fee ?? {};
    },
    setConfirmEquipFee: (state, { payload }) => {
      state.confirmEquipFee = payload;
    },
    setCheckoutForm: (state, { payload }) => {
      state.checkoutForm = payload;
    },
    resetCheckoutForm: (state) => {
      state.checkoutForm = {};
    },
    resetBookingSession: (state) => {
      state.confirmId = '';
      state.bookingId = 0;
      state.bookingRefId = '';
      state.confirmItems = [];
      state.confirmEquipFee = {};
    },
    setPartnerInfo: (state, { payload }) => {
      state.partnerInfo = payload;
    },
    setPartnerStore: (state, { payload }) => {
      state.partnerStore = payload;
    },
    setPartnerTrackCode: (state, { payload }) => {
      state.partnerTrackCode = payload;
    },
    setCustomerCheckId: (state, { payload }) => {
      state.customerCheckId = payload;
    },
    setCOValidation: (state, { payload }) => {
      state.coValidation = payload;
    },
    setCustomerId: (state, { payload }) => {
      state.customerId = payload;
    },
    setCustomerAddId: (state, { payload }) => {
      state.customerAddId = payload;
    },
    setBookingId: (state, { payload }) => {
      state.bookingId = payload;
    },
    setBookingRefId: (state, { payload }) => {
      state.bookingRefId = payload;
    },
    setConfirmId: (state, { payload }) => {
      state.confirmId = payload;
    },
    setIsLoading: (state, { payload }) => {
      state.isLoading = payload;
    },
    setLocations: (state, { payload }) => {
      state.locations = payload;
    },
    setSelLocation: (state, { payload }) => {
      state.selLocation = payload;
    },
	setDiscountCode: (state, { payload }) => {
	  state.discountCode = payload;
	},
    setAddonServicesForAsset: (state, { payload }) => {
      const assetId = String(payload?.assetId ?? "");
      if (!assetId) return;
      state.addonServicesByAssetId[assetId] = Array.isArray(payload?.services) ? payload.services : [];
    },
    setSelectedAddonServiceIdsForAsset: (state, { payload }) => {
      const assetId = String(payload?.assetId ?? "");
      if (!assetId) return;
      const ids = Array.isArray(payload?.serviceIds) ? payload.serviceIds : [];
      state.selectedAddonServiceIdsByAssetId[assetId] = ids
        .map((x: any) => Number(x))
        .filter((n: number) => Number.isFinite(n));
    },
    setSelectedCheckoutLocationId: (state, { payload }) => {
      state.selectedCheckoutLocationId = payload ? String(payload) : null;
    },
  }
});

export const {
  setHeaderTheme,
  setShowInfoModal,
  setEquipInfo,
  setEquipRate,
  setEquipFee,
  setEquipFeeByAssetId,
  setEquipFeeForAsset,
  setAddCart,
  addCartItem,
  replaceCartItemAtLocation,
  setCartItems,
  setConfirmEquipFee,
  setConfirmItems,
  setRemoveCart,
  removeCartItemByAssetId,
  removeCartItemsAtLocation,
  removeCartItemsByAssetIds,
  setFilterPickupDate,
  setFilterReturnDate,
  setFilterPickupTime,
  setFilterReturnTime,
  setFilterDate,
  setPartnerInfo,
  setPartnerStore,
  setPartnerTrackCode,
  setCheckoutForm,
  resetCheckoutForm,
  resetBookingSession,
  setCustomerCheckId,
  setCOValidation,
  setCustomerId,
  setCustomerAddId,
  setBookingId,
  setBookingRefId,
  setConfirmId,
  setIsLoading,
  setLocations,
  setSelLocation,
  setDiscountCode,
  setAddonServicesForAsset,
  setSelectedAddonServiceIdsForAsset,
  setSelectedCheckoutLocationId,
} = settingSlice.actions;
export default settingSlice.reducer;
